import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { ChartService } from 'src/app/services/chart.service';
import * as charts from 'highcharts';
import { AppConstants } from 'src/app/constants/app-constants';
import { AnalyticalDashboardService } from 'src/app/services/analytical-dashboard.service';
import { DateUtil } from 'src/app/utils/date-util';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { Utility } from 'src/app/utils/app-utils';
import { TranslateService } from '@ngx-translate/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

@Component({
	selector: 'app-operator-stats',
	templateUrl: './operator-stats.component.html',
	styleUrls: ['./operator-stats.component.scss']
})
export class OperatorStatsComponent implements OnInit, AfterViewInit {
	KPICharts: charts.Chart;
	KPI_CHART_ID = 'kpi-chart';
	KPI_CHART_Title = 'kpi-chart';
	operatorsHistoryDataSource: MatTableDataSource<any> = new MatTableDataSource([]);
	public selectedOperatorsHistoryId = null;
	public selectedOperatorsHistory: any;
	selectedAlarmIDs = [];
	filterType = 'OPERATORS';
	appConstants = AppConstants;
	displayedColumns = ['Operators', 'Region', 'RelatedSites', 'Sites', 'Status'];
	operatorsStatsChangeSubscriber: any;
	@ViewChild(MatSort, { static: false }) sort: MatSort;
	constructor(
		public chartService: ChartService,
		public analyticalDashboard: AnalyticalDashboardService,
		public authService: AuthenticationService,
		public translate: TranslateService
	) { }

	async ngOnInit() {
		this.getOperatorsStats();
		this.operatorsStatsChangeSubscriber = this.analyticalDashboard.getOperatorsStatsChangeNotification().subscribe(() => {
			this.operatorsHistoryDataSource.data = this.analyticalDashboard.operatorsReport;
			setTimeout(() => {
				this.connectSort();
			}, 0);
			if (this.operatorsHistoryDataSource.data.length) {
				this.selectedOperatorsHistoryId = this.operatorsHistoryDataSource.data[0].id;
				this.selectedOperatorsHistory = this.operatorsHistoryDataSource.data[0];
				this.analyticalDashboard.selectAlarm(this.selectedOperatorsHistory);
			}
		});
		await this.analyticalDashboard.selectAlarm(this.selectedOperatorsHistory);
	}

	ngAfterViewInit() {
		setTimeout(() => {
			this.connectSort();
		}, 0);
	}

	/**
	 * Connect MatSort to the data source and configure custom(alphabetical and numeric) sorting
	 */
	private connectSort() {
		this.operatorsHistoryDataSource.sortingDataAccessor = (item, property) => {
			let value: any;
			switch (property) {
				case 'Operators':
					value = (item.operatorName || '').toString().toLowerCase();
					break;
				case 'Region':
					value = (this.formatRegionsNames(item.regions) || '').toString().toLowerCase();
					break;
				case 'RelatedSites':
					value = (this.formatSitesNames(item.relatedSites) || '').toString().toLowerCase();
					break;
				case 'Sites':
					// Keep as number for numeric sorting
					value = this.formatSitesCounter(item.relatedSites) || 0;
					break;
				case 'Status':
					value = (item.operatorStatus || '').toString().toLowerCase();
					break;
				default:
					value = item[property];
					// If it's a string, normalize it for alphabetical sorting
					if (typeof value === 'string') {
						value = value.toLowerCase();
					}
			}
			return value;
		};

		// Connect sort if available
		if (this.sort) {
			this.operatorsHistoryDataSource.sort = this.sort;
		}
	}

	ngOnDestroy() {
		this.operatorsStatsChangeSubscriber = Utility.Unsubscribe(this.operatorsStatsChangeSubscriber);
	}

	/**
	 * used to render KPI chart
	 * call it to show KPI chart
	 */
	renderKPIChart() {
		this.KPICharts = this.chartService.renderKPIChart(this.KPI_CHART_ID, this.translate.instant('ANALYTICAL_CHARTS.CHART_TITLE_KPI'));
	}

	/**
	 * called to highlight row in table and zoom to map
	 */
	highlight(row: any) {
		this.selectedOperatorsHistoryId = row.id;
		this.selectedOperatorsHistory = row;
		this.analyticalDashboard.selectAlarm(this.selectedOperatorsHistory);
	}

	/**
	 * called to get all operators stats
	 */
	async getOperatorsStats() {
		const response = await this.analyticalDashboard.getOperatorsReport();
		this.operatorsHistoryDataSource.data = response;
		setTimeout(() => {
			this.connectSort();
		}, 0);
		if (this.operatorsHistoryDataSource.data.length) {
			this.selectedOperatorsHistoryId = this.operatorsHistoryDataSource.data[0].id;
			this.selectedOperatorsHistory = this.operatorsHistoryDataSource.data[0];
			this.analyticalDashboard.selectAlarm(this.selectedOperatorsHistory);
		}
	}

	/**
	 * called to format time from millisecond to required format
	 */
	getFormatedTime(activationTime: any, format: any) {
		return DateUtil.getFormattedDate(activationTime, format, this.authService.getUserTimezone());
	}

	/**
	 * format multiple operators name by comma
	 */
	getOperatorName(alarmsHistory: any) {
		return alarmsHistory.operator ? `${alarmsHistory.operator.firstName} ${alarmsHistory.operator.lastName}` : '';
	}

	/**
	 * format multiple regions name by comma
	 */
	formatRegionsNames(regions: any) {
		return regions ? regions.map(regions => `${regions}`).join(', ') : '';
	}

	/**
	 * format multiple sites name by comma
	 */
	formatSitesNames(operators: any) {
		return operators ? operators.map(operator => `${operator.siteName}`).join(', ') : '';
	}

	/**
	 * called to get no. of sites
	 */
	formatSitesCounter(operator: any) {
		return operator ? operator.length : 0;
	}

	/**
	 * function called then map loading is done
	 */
	mapLoaded() {
		this.analyticalDashboard.selectAlarm(this.selectedOperatorsHistory);
	}

	/**
	 * called to change height of operator table to make it responsive
	 */
	changeTableSize() {
		this.analyticalDashboard.changeTableSizeOnAddedFilter(AppConstants.OPERATOR_TABLE_CONTAINER);
	}
}
